import { SpriteIcon } from "@components/core/icons/SpriteIcon";
import { AddToCart, AddToPlaylist } from "@components/interaction";
import { renderArtistNames } from "@components/shared/Artists/ArtistNames";
import LabelLink from "@components/shared/Labels/LabelLink";
import { TRACK_PLACEHOLDER } from "@lib/constants";
import { useMediaQuery } from "@lib/hooks/useMediaQuery";
import { msToClock } from "@lib/utils";
import { Track } from "@models/track";
import { device } from "@styles/theme";
import Image from "next/image";
import Link from "next/link";
import {
	ActionsWrapper,
	Artists,
	BpmWrapper,
	ClearBox,
	GenreWrapper,
	IconWrapper,
	LabelWrapper,
	MixName,
	NameWrapper,
	PlaylistContainer,
	PlaylistItem,
	TrackName,
	Wrapper,
} from "./Playlist.style";

const FALLBACK_URL = TRACK_PLACEHOLDER.href;
const IMG_WIDTH = 44;
const IMG_HEIGHT = 44;

interface Props {
	current?: Track;
	location?: string;
	tracks: Track[];
	onRemoveTrackClick: (track: Track, index: number) => void;
	onClearPlaylistClick: () => void;
	onPlaylistPlayTrackClick: (track: Track) => void;
	setPlaylistVisible: (visible: boolean) => void;
}

const Playlist: React.FC<Props> = ({
	current,
	location,
	tracks,
	onRemoveTrackClick,
	onClearPlaylistClick,
	onPlaylistPlayTrackClick,
	setPlaylistVisible,

}) => {
	const isLG = useMediaQuery({ query: device.lg });
	const isMD = useMediaQuery({ query: device.md });
	const isSM = useMediaQuery({ query: device.sm });

	const isCurrent = (t: Track): boolean => {
		if (!current) return false;

		if (current.unique_player_id) return current.unique_player_id === t.unique_player_id;
		return current.id === t.id;
	};

	return (
		<Wrapper>
			<ClearBox>
				<a
					title="Clear queue"
					onClick={(e) => {
						e.preventDefault();
						setPlaylistVisible(false);
						onClearPlaylistClick();
					}}
				>
					Clear queue
				</a>
			</ClearBox>
			<PlaylistContainer>
				{tracks.map((track, index) => (
					<PlaylistItem
						key={`player-playlist-track-${track.id}-${index}`}
						className={
							isCurrent(track) ?
								"current player-playlist-track-current" :
								undefined
						}
					>
						<Image
							src={track.release.image?.uri}
							alt={track.release.name}
							width={IMG_WIDTH}
							height={IMG_HEIGHT}
							style={{ minWidth: IMG_WIDTH, margin: "0.25rem" }}
							blurDataURL={FALLBACK_URL}
							placeholder="blur"
							className="cover"
							onClick={(e) => {
								e.preventDefault();
								onPlaylistPlayTrackClick(track);
							}}
						/>
						<div className="track-play-overlay">
							<SpriteIcon id="track-play-overlay" className="icon" />
						</div>

						<NameWrapper>
							<Link
								href={`/track/${track.slug}/${track.id}`}
								prefetch={false}
								title={track.name}
								onClick={(e) => {
									e.stopPropagation();
								}}
							>
								<TrackName className="track-name">{track.name} </TrackName>
								<MixName>{track.mix_name}</MixName>
							</Link>
							{track.artists &&
								track.artists.length > 0 && (
								<Artists>{ renderArtistNames(track.artists, { location }) }</Artists>
							)}
						</NameWrapper>
						{isLG && (
							<GenreWrapper>
								<Link
									href={`/genre/${track.genre.slug}/${track.genre.id}`}
									prefetch={false}
									title={track.genre.name}
									onClick={(e) => {
										e.stopPropagation();
									}}
								>
									{track.genre.name}
								</Link>
								<span>{msToClock(track.length_ms)}</span>
							</GenreWrapper>
						)}
						{isSM && (
							<BpmWrapper>
								<span>{track.key?.name}</span>
								<span>{track.bpm} BPM</span>
							</BpmWrapper>
						)}
						{isMD && (
							<LabelWrapper>
								<LabelLink
									label={track.release.label}
									location={location}
									withMarquee
									onClick={(ev) => { ev.stopPropagation(); }}
								/>
								<span>{track.new_release_date}</span>
							</LabelWrapper>
						)}
						<ActionsWrapper>
							<IconWrapper>
								<AddToPlaylist tracks={[track]} />
							</IconWrapper>
							<AddToCart location={location} iconOnly={!isMD} track={track} />
							<a
								title="remove from playlist"
								onClick={(e) => {
									e.stopPropagation();
									onRemoveTrackClick(track, index);
								}}
							>
								<SpriteIcon id="close-x" width="18" height="18" />
							</a>
						</ActionsWrapper>
					</PlaylistItem>
				))}
			</PlaylistContainer>
		</Wrapper>
	);
};

export default Playlist;
